// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { arrayOf, func, number, object, bool } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { ComposedModal, ModalHeader, ModalBody } from '@carbon/react';
import uniqueId from 'lodash/uniqueId';

import { renderPrevArrow, renderNextArrow } from 'Components/shared';

import Img from 'Containers/Image/Image';

const StyledComposedModal = styled(ComposedModal)`
  .cds--modal-container {
    @media screen and (min-width: 1680px) {
      width: 95vw;
      height: 95vh;
    }
    //height: 95vh;
    //width: 95vw;
    max-height: none;
    max-width: none;
  }

  .cds--modal-header {
    background-color: transparent;
  }

  .cds--modal-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 95%;
  }
`;

const StyledCarouselContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  .soloImageContainer {
    width: 100%;
    height: 100%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;

    > img {
      display: block;
      height: auto;
      margin: 0 auto;
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  @media screen and (max-width: 671px) {
    flex-direction: column-reverse;
  }

  @media screen and (max-width: 671px) {
    width: 100%;
    min-width: 100%;
    margin-right: 0;
  }

  .sliderRoot {
    position: relative;
    flex-grow: 1;
    width: 100%;

    .control-arrow {
      &.control-prev {
        &:before {
          margin: 0;
          border: 0;
          font-size: 20px;
          display: block;
          /* font-family: revicons; */
          position: relative;
          content: "\\e824";
          text-align: center;
          z-index: 2;
        }
      }
      &.control-next {
        &:before {
          margin: 0;
          border: 0;
          font-size: 20px;
          display: block;
          /* font-family: revicons; */
          position: relative;
          content: "\\e825";
          text-align: center;
          z-index: 2;
        }
      }

      width: 30px;
      height: 75px;
      border-radius: 0;
      opacity: 0.45;
      -webkit-transition: opacity 0.05s ease;
      transition: opacity 0.05s ease;
      margin: 0;
      top: 50%;
      transform: translateY(-50%);
      color: ${ props => props.theme.colors.blue };
      background-color: ${ props => props.theme.colors.shadowGray };
    }
  }

  .badges {
    position: absolute;
    top: 5px;
    left: 10px;
    width: auto;
    z-index: 9;

    .badge {
      border-radius: 0;
      background-color: transparent;
      color: #E8720B;
    }
  }

  .thumbs-container {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;
    height: fit-content;
    margin: -5px 10px 0 0;
    display: grid;
    grid-template-columns: repeat(${ props => props.columns || 1 }, 1fr);
    grid-column-gap: 10px;

    @media screen and (max-width: 960px) {
      grid-template-columns: repeat(1, 1fr);
    }

    @media screen and (max-width: 671px) {
      width: 100%;
      margin: 10px 0 0;
      display: flex;
    }

    li {
      &:first-of-type {
        margin-left: 0;
      }

      &:last-of-type {
        margin-right: 0;
      }

      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100px;
      min-width: 100px;
      width: 100px;
      height: 100px;
      margin: 5px 0;
      padding: 5px;
      border: 1px solid ${ props => props.theme.products.colors.border };
      box-sizing: border-box;
      cursor: pointer;

      img {
        object-fit: contain;
        max-height: 100%;
        max-width: 100%;
        width: auto;
        user-select: none;
      }
    }
  }
`;

const StyledCarousel = styled(Carousel)`
  //display: flex;
  //flex-direction: row-reverse;
  //justify-content: space-between;
  //align-items: stretch;
  //max-height: 460px;
  height: 100%;

  .carousel {
    width: auto;
    margin-right: 1.5rem;
  }

  .carousel-slider {
    //max-width: 600px;
    flex-grow: 1;
    height: 100%;
    margin: 0;
    border: 1px solid ${ props => props.theme.products.colors.border };
    justify-content: center;
    align-items: center;
  }

  .slider-wrapper {
    height: 100%;
    cursor: pointer;
  }

  .slider {
    height: 100%;
  }

  .slide {
    padding: 0.875rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .placeholder {
      position: relative;
      display: block;
      margin: 0 auto;
      height: auto;
      width: auto;
      max-width: 65%;
      max-height: 70%;
    }

    img {
      display: block;
      height: auto;
      max-height: 90%;
      max-width: 100%;
      margin: 0 auto;
      width: auto;
      user-select: none;
    }
  }
`;

const ProductPicturesModalComponent = ({ open, onChange, onClose, selected, pictures = [] }) => {
  const theme = useTheme();

  const memoizedCarouselItems = useMemo(
    () => pictures.map(picture => (
      <Img
        key={ uniqueId(picture.urlOverlayPicture) }
        src={ picture.urlOverlayPicture }
        loader={ <img src={ picture.urlThumbnailPicture } alt="" /> }
      />
    )),
    [pictures]
  );

  const memoizedThumbnails = useMemo(
    () => pictures.map((picture, idx) => (
      <li aria-hidden="true" key={ uniqueId() } onClick={ () => onChange(idx) }>
        <Img
          key={ uniqueId(picture.urlThumbnailPicture) }
          src={ picture.urlThumbnailPicture }
        />
      </li>
    )),
    [pictures]
  );

  return (
    <StyledComposedModal
      open={ open }
      size="lg"
      onClose={ onClose }
      className="ProductPicturesModal"
      selectorPrimaryFocus=".cds--modal-content"
    >
      <ModalHeader />
      <ModalBody>
        <StyledCarouselContainer
          className="carouselContainer"
          theme={ theme }
          columns={ pictures.length >= 6 ? 2 : 1 }
        >
          { (pictures.length > 1) && (
            <ul className="thumbs-container">
              { memoizedThumbnails }
            </ul>
          ) }
          { pictures.length > 1
            ? (
              <div className="sliderRoot">
                <StyledCarousel
                  theme={ theme }
                  emulateTouch
                  swipeable
                  showStatus={ false }
                  showIndicators={ false }
                  dynamicHeight={ false }
                  showArrows
                  autoPlay={ false }
                  showThumbs={ false }
                  onClickItem={ onChange }
                  selectedItem={ selected }
                  renderArrowPrev={ renderPrevArrow }
                  renderArrowNext={ renderNextArrow }
                >
                  { memoizedCarouselItems }
                </StyledCarousel>
              </div>
            )
            : (
              <div className="soloImageContainer">
                { memoizedCarouselItems }
              </div>
            )
          }
        </StyledCarouselContainer>
      </ModalBody>
    </StyledComposedModal>
  );
};

ProductPicturesModalComponent.displayName = 'ProductPicturesModalComponent';
ProductPicturesModalComponent.propTypes = {
  open: bool,
  onClose: func,
  onChange: func,
  selected: number,
  pictures: arrayOf(object)
};

export default ProductPicturesModalComponent;
